import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import api from '../utils/api';

export interface Message {
  id: number;
  sender_id: number;
  receiver_id?: number;
  conversation_id?: number;
  organization_id: number;
  content: string;
  message_type: 'text' | 'broadcast' | 'sms' | 'email';
  message_id?: string;
  parent_message_id?: number;
  is_read: boolean;
  is_deleted: boolean;
  read_at?: string;
  created_at: string;
  updated_at: string;
  sender?: {
    id: number;
    username: string;
    contact?: {
      first_name: string;
      last_name: string;
    };
  };
  receiver?: {
    id: number;
    username: string;
    contact?: {
      first_name: string;
      last_name: string;
    };
  };
}

export interface BroadcastMessage {
  organization_id: number;
  plot_id?: number;
  content: string;
  message_type?: string;
  is_broadcast: boolean;
}

export interface DirectMessage {
  receiver_id: number;
  content: string;
  message_type: 'text' | 'sms' | 'email';
}

export interface GroupMessage {
  conversation_id: number;
  content: string;
  message_type: 'text' | 'sms' | 'email';
}

export interface ReplyMessage {
  parent_message_id: number;
  content: string;
  message_type: 'text' | 'sms' | 'email';
}

interface MessageStore {
  messages: Message[];
  broadcastMessages: Message[];
  currentMessage: Message | null;
  isLoading: boolean;
  error: string | null;
  
  // Message fetching
  fetchMessages: (limit?: number, offset?: number) => Promise<Message[]>;
  fetchBroadcastMessages: (orgId: number, limit?: number, offset?: number) => Promise<Message[]>;
  fetchConversationMessages: (conversationId: number, limit?: number, offset?: number) => Promise<Message[]>;
  fetchDirectMessages: (userId: number, limit?: number, offset?: number) => Promise<Message[]>;
  fetchUserConversations: () => Promise<Message[]>;
  fetchRecentMessages: (limit?: number) => Promise<Message[]>;
  fetchUnreadCount: () => Promise<number>;
  fetchMessageStats: () => Promise<any>;
  
  // Message sending
  sendBroadcastMessage: (message: BroadcastMessage) => Promise<Message>;
  sendDirectMessage: (message: DirectMessage) => Promise<Message>;
  sendGroupMessage: (message: GroupMessage) => Promise<Message>;
  replyToMessage: (message: ReplyMessage) => Promise<Message>;
  
  // Message actions
  markMessagesAsRead: (messageIds: number[]) => Promise<void>;
  searchMessages: (query: string, limit?: number) => Promise<Message[]>;
  
  // State management
  setCurrentMessage: (message: Message | null) => void;
  clearError: () => void;
}

export const useMessageStore = create<MessageStore>()(
  devtools(
    (set, get) => ({
      messages: [],
      broadcastMessages: [],
      currentMessage: null,
      isLoading: false,
      error: null,

      // Fetch all messages
      fetchMessages: async (limit = 50, offset = 0) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/messages', {
            params: { limit, offset }
          });
          set({ messages: response.data, isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch messages';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Fetch broadcast messages for an organization
      fetchBroadcastMessages: async (orgId: number, limit = 50, offset = 0) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get(`/messages/broadcast/${orgId}`, {
            params: { limit, offset }
          });
          set({ broadcastMessages: response.data, isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch broadcast messages';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Fetch conversation messages
      fetchConversationMessages: async (conversationId: number, limit = 50, offset = 0) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get(`/messages/conversation/${conversationId}`, {
            params: { limit, offset }
          });
          set({ messages: response.data, isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch conversation messages';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Fetch direct messages between users
      fetchDirectMessages: async (userId: number, limit = 50, offset = 0) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get(`/messages/direct/${userId}`, {
            params: { limit, offset }
          });
          set({ messages: response.data, isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch direct messages';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Fetch user conversations
      fetchUserConversations: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/messages/conversations');
          set({ messages: response.data, isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch conversations';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Fetch recent messages
      fetchRecentMessages: async (limit = 10) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/messages/recent', {
            params: { limit }
          });
          set({ messages: response.data, isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch recent messages';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Fetch unread count
      fetchUnreadCount: async () => {
        try {
          const response = await api.get('/messages/unread-count');
          return response.data.unread_count || 0;
        } catch (error: any) {
          console.error('Failed to fetch unread count:', error);
          return 0;
        }
      },

      // Fetch message stats
      fetchMessageStats: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/messages/stats');
          set({ isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to fetch message stats';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Send broadcast message
      sendBroadcastMessage: async (message: BroadcastMessage) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/messages/broadcast', message);
          set({ isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to send broadcast message';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Send direct message
      sendDirectMessage: async (message: DirectMessage) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/messages/send-direct', message);
          set({ isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to send direct message';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Send group message
      sendGroupMessage: async (message: GroupMessage) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/messages/send-group', message);
          set({ isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to send group message';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Reply to message
      replyToMessage: async (message: ReplyMessage) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/messages/reply', message);
          set({ isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to reply to message';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // Mark messages as read
      markMessagesAsRead: async (messageIds: number[]) => {
        try {
          await api.post('/messages/mark-read', { message_ids: messageIds });
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to mark messages as read';
          set({ error: errorMessage });
          throw new Error(errorMessage);
        }
      },

      // Search messages
      searchMessages: async (query: string, limit = 20) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.get('/messages/search', {
            params: { query, limit }
          });
          set({ messages: response.data, isLoading: false });
          return response.data;
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Failed to search messages';
          set({ error: errorMessage, isLoading: false });
          throw new Error(errorMessage);
        }
      },

      // State management
      setCurrentMessage: (message: Message | null) => {
        set({ currentMessage: message });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'message-store',
    }
  )
);

export default useMessageStore;
